import { useState, useRef } from 'react'
import { X, AlertCircle, Loader } from 'lucide-react'
import { projectsApi } from '../lib/projectsApi'
import { SecureInput, SecureTextarea } from './SecureInput'
import { scanInput } from '../lib/inputSecurity'

export function CreateProjectModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    overview: '',
    image: null,
  })
  const [imagePreview, setImagePreview] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const fileInputRef = useRef(null)

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    setError(null)
  }

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      setFormData((prev) => ({
        ...prev,
        image: file,
      }))
      
      // Create preview
      const reader = new FileReader()
      reader.onload = (event) => {
        setImagePreview(event.target.result)
      }
      reader.readAsDataURL(file)
      setError(null)
    }
  }

  const handleRemoveImage = () => {
    setFormData((prev) => ({
      ...prev,
      image: null,
    }))
    setImagePreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    if (!formData.name.trim()) { setError('Project name is required'); return }
    const secErr = scanInput(formData.name) || scanInput(formData.description) || scanInput(formData.overview)
    if (secErr) { setError(secErr); return }

    setIsLoading(true)
    try {
      const result = await projectsApi.createProject(
        formData.name,
        formData.description,
        formData.overview,
        formData.image
      )

      if (result.success) {
        // Reset form
        setFormData({
          name: '',
          description: '',
          overview: '',
          image: null,
        })
        setImagePreview(null)
        
        // Call success callback
        if (onSuccess) {
          onSuccess(result.data)
        }
        
        // Close modal
        onClose()
      } else {
        setError(result.error || 'Failed to create project')
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to create project')
      console.error('Create project error:', err)
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-2xl font-bold text-gray-900">Create Project</h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
          >
            <X size={24} />
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Project Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Project Name <span className="text-red-600">*</span>
            </label>
            <SecureInput
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter project name"
              disabled={isLoading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description
            </label>
            <SecureInput
              type="text"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter project description"
              disabled={isLoading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
            />
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Cover Photo
            </label>
            {imagePreview ? (
              <div className="relative">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full h-40 object-cover rounded-lg"
                />
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  disabled={isLoading}
                  className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-2 disabled:opacity-50"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
                className="w-full py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition disabled:opacity-50"
              >
                <div className="text-center">
                  <p className="text-gray-600 font-medium">Drag and Drop Files or</p>
                  <p
                    className="font-semibold"
                    style={{ color: '#358ECD' }}
                  >
                    Browse
                  </p>
                </div>
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              disabled={isLoading}
              style={{ display: 'none' }}
            />
          </div>

          {/* Overview */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Overview
            </label>
            <SecureTextarea
              name="overview"
              value={formData.overview}
              onChange={handleInputChange}
              placeholder="Enter project overview"
              rows={4}
              disabled={isLoading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 resize-none"
            />
          </div>

          {/* License Note */}
          <div className="text-sm text-gray-600">
            <p>
              <strong>Project Tier:</strong> <span style={{ color: '#22c55e' }}>Professional</span>
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 transition flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ backgroundColor: '#358ECD' }}
            >
              {isLoading && <Loader size={16} className="animate-spin" />}
              {isLoading ? 'Creating...' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
